import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_CMS_SERVICE } from '@/api/constant';
import CategoryTable from '@/components/category_brands/CategoryTable';
import ErrorMessage from '@/components/common/ErrorMessage';
import Layout from '@/components/Layout/Layout';
import ProductTable from '@/components/product/ProductTable';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import UploadField from '@/components/UI/UploadField';
import { ICategoryData } from '@/interfaces/categoryResponse.interface';
import { IContentData } from '@/interfaces/contentResponse.interface';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import { IproductResponse } from '@/interfaces/productresponse.interface';
import { ContentKey, ContentType } from '@/utility/enum';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

interface ContentFormProp {
  contentKey: string;
  contentType: string;
  valueText: string;
  redirectUrl: string;
}

const AddContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mediaFile, setMediaFile] = useState<IMediaProp | null>(null);
  const [selectedData, setSelectedData] = useState<
    IproductResponse[] | ICategoryData[]
  >([]);
  const [initialId, setInitialId] = useState<string | undefined>(undefined);
  const {
    register,
    watch,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<ContentFormProp>({
    defaultValues: {
      contentKey: '',
      contentType: '',
      valueText: '',
      redirectUrl: '',
    },
  });

  const contentKey = watch('contentKey');
  const contentType = watch('contentType');

  useEffect(() => {
    if (
      contentType === ContentType.VIDEO &&
      contentKey === ContentKey.HOME_PAGE_BANNER
    ) {
      setError('contentType', {
        message: 'Content can not be home banner for the videos',
      });
    } else if (
      contentType === ContentType.VIDEO &&
      contentKey === ContentKey.POP_UP
    ) {
      setError('contentType', {
        message: 'Content can not be popup for the videos',
      });
    } else {
      clearErrors('contentType');
    }
  }, [contentKey, contentType, setError, clearErrors]);

  const redirection = watch('redirectUrl');

  const redirectTypes = [
    { label: 'Category', value: 'category' },
    { label: 'Product', value: 'product' },
  ];

  const contentTypes = [
    { label: 'Image', value: ContentType.IMAGE },
    { label: 'Video', value: ContentType.VIDEO },
  ];

  const contentkeys = [
    { label: 'Front Banner', value: ContentKey.LOGIN_BACKGROUND },
    { label: 'Home Banner', value: ContentKey.HOME_PAGE_BANNER },
    { label: 'Pop Up', value: ContentKey.POP_UP },
  ];

  const fetchContentDetail = async () => {
    try {
      const PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.content}/${id}`;
      const res = await callApi<IContentData>(PATH, { method: 'GET' });
      const [type, uuid] = res.redirectUrl.split('/');
      setInitialId(uuid);
      reset({
        ...res,
        redirectUrl: type,
      });
      const mediaType = res.valueUrl.match(/\.(mp4|webm|mov)$/i)
        ? 'video'
        : 'image';
      setMediaFile({
        url: res.valueUrl,
        altText: '',
        type: mediaType,
      });
    } catch (error) {
      console.log('error in fetch content detail', error);
    }
  };

  const createContent = async (data: ContentFormProp) => {
    try {
      let valueUrl = mediaFile && mediaFile?.url;
      let cleanedUrl =
        valueUrl && valueUrl.replace(`${API_URL_CMS_SERVICE}/`, '');
      const postData = {
        contentKey: data.contentKey,
        contentType: data.contentType,
        valueText: data.valueText,
        redirectUrl: `${data.redirectUrl}/${selectedData[0]?.id}`,
        ...(mediaFile?.url ? { valueUrl: cleanedUrl } : {}),
      };
      let PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.content}`;
      if (id) {
        PATH = `${PATH}/${id}`;
      }
      await callApi(PATH, { method: id ? 'PATCH' : 'POST', data: postData });
      navigate(RoutesLink.websiteContent);
    } catch (error) {
      console.log('error in content creation', error);
    }
  };

  useEffect(() => {
    if (id) fetchContentDetail();
  }, [id]);

  return (
    <Layout>
      <form onSubmit={handleSubmit(createContent)}>
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
            <div>
              <FormField
                label="Content Key"
                type="select"
                options={contentkeys}
                {...register('contentKey', {
                  ...errorTypes.required('Content key'),
                })}
              />
              <ErrorMessage message={errors.contentKey?.message} />
            </div>
            <div>
              <FormField
                label="Content Type"
                type="select"
                options={contentTypes}
                placeholder="Select Content Type"
                {...register('contentType', {
                  ...errorTypes.required('Content type'),
                })}
              />
              <ErrorMessage message={errors.contentType?.message} />
            </div>
            <div>
              <FormField
                label="Title"
                placeholder="Title here..."
                {...register('valueText', {
                  ...errorTypes.required('Title'),
                })}
              />
              <ErrorMessage message={errors.valueText?.message} />
            </div>
            <FormField
              label="Redirect Url"
              type="select"
              options={redirectTypes}
              placeholder="Select Redrication Type"
              {...register('redirectUrl')}
            />
          </div>
          <div className="">
            <UploadField
              multiple={false}
              apiType="content"
              label="Upload Media File"
              referenceType=""
              accept={contentType === ContentType.VIDEO ? 'video/*' : 'image/*'}
              initialMedia={id && mediaFile ? [mediaFile] : []}
              onChangeMedia={(media) => {
                setMediaFile(media[0]);
              }}
            />
          </div>
          {redirection === 'product' && (
            <ProductTable
              selectionMode="single"
              initialSelectedIds={initialId ? [initialId] : undefined}
              onSelectProducts={setSelectedData}
            />
          )}

          {redirection === 'category' && (
            <CategoryTable
              selectionMode="single"
              initialSelectedIds={initialId ? [initialId] : undefined}
              onSelectCtegory={setSelectedData}
            />
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => navigate(-1)}
              type="button"
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </Card>
      </form>
    </Layout>
  );
};

export default AddContent;
